/**
 * Gestos que um app tem e uma página não.
 *
 * **Sem pinça.** `user-scalable=no` no viewport resolve o Android; o iOS
 * ignora a meta desde o iOS 10, então o `gesturestart` (evento próprio do
 * WebKit para a pinça) é cancelado aqui. Escolha do PO: o Viajandos é app, e
 * app não amplia a tela (teste físico, item 4). O custo — quem precisa
 * ampliar não consegue — está registrado; a alternativa é o zoom do sistema.
 *
 * **Voltar pela borda.** O app não tem botão "‹" (item 19): no Android o
 * gesto e o botão do sistema voltam; no iOS instalado como PWA não existe
 * gesto de voltar, então arrastar da borda esquerda faz `history.back()`.
 */

import { isStandalone } from '../install/detect';

const BORDA_PX = 24;
const ARRASTO_MIN_PX = 70;

export function instalarGestosDeApp(): void {
  if (typeof window === 'undefined') return;

  for (const tipo of ['gesturestart', 'gesturechange', 'gestureend']) {
    window.addEventListener(tipo, (e) => e.preventDefault(), { passive: false });
  }

  // Toque duplo também amplia no iOS; `touch-action: manipulation` no CSS
  // cuida disso onde o navegador respeita, e este é o reforço.
  let ultimoToque = 0;
  window.addEventListener(
    'touchend',
    (e) => {
      const agora = Date.now();
      if (agora - ultimoToque < 300 && e.touches.length === 0) e.preventDefault();
      ultimoToque = agora;
    },
    { passive: false },
  );

  // Só quando instalado: no Safari e no Chrome "de navegador" o próprio
  // navegador já volta pela borda, e dois "voltar" seriam um a mais.
  if (!isStandalone(window.matchMedia, (navigator as Navigator & { standalone?: boolean }).standalone)) return;

  let inicio: { x: number; y: number } | null = null;
  window.addEventListener(
    'touchstart',
    (e) => {
      const t = e.touches[0];
      inicio = e.touches.length === 1 && t && t.clientX <= BORDA_PX ? { x: t.clientX, y: t.clientY } : null;
    },
    { passive: true },
  );
  window.addEventListener(
    'touchend',
    (e) => {
      if (!inicio) return;
      const t = e.changedTouches[0];
      const dx = (t?.clientX ?? 0) - inicio.x;
      const dy = Math.abs((t?.clientY ?? 0) - inicio.y);
      inicio = null;
      if (dx >= ARRASTO_MIN_PX && dy < dx / 2 && window.history.length > 1) window.history.back();
    },
    { passive: true },
  );
}
